import { useState } from 'react'
import { patientsService } from '../../services/api/patients'
import type { InsStatut, InsData, Gender } from '../../types'

interface InsSectionProps {
  patientId: number
  insStatut?: InsStatut
  insMatricule?: string
  onUpdated: () => void
}

const OID_NIR = '1.2.250.1.213.1.4.8'
const OID_NIA = '1.2.250.1.213.1.4.9'

const STATUT_CONFIG: Record<
  InsStatut,
  { label: string; color: string; bg: string }
> = {
  PROVISOIRE: { label: 'Provisoire', color: '#B45309', bg: '#FEF3C7' },
  QUALIFIE: { label: 'Qualifié', color: '#15803D', bg: '#DCFCE7' },
  INVALIDE: { label: 'Invalide', color: '#DC2626', bg: '#FEE2E2' },
}

export default function InsSection({
  patientId,
  insStatut = 'PROVISOIRE',
  insMatricule,
  onUpdated,
}: InsSectionProps) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [form, setForm] = useState<InsData>({
    insMatricule: '',
    insOid: OID_NIR,
    birthLastName: '',
    birthFirstName: '',
    birthDate: '',
    birthPlace: '',
    gender: 'M',
  })

  const cfg = STATUT_CONFIG[insStatut]

  const handleQualify = async () => {
    setError('')
    if (
      !form.insMatricule ||
      !form.birthLastName ||
      !form.birthFirstName ||
      !form.birthDate ||
      !form.birthPlace
    ) {
      setError('Tous les champs sont obligatoires.')
      return
    }
    setLoading(true)
    try {
      await patientsService.qualifyIns(patientId, form)
      setOpen(false)
      onUpdated()
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : 'Erreur lors de la qualification.'
      )
    } finally {
      setLoading(false)
    }
  }

  const handleInvalidate = async () => {
    if (!window.confirm("Confirmer l'invalidation de l'INS ?")) return
    setLoading(true)
    try {
      await patientsService.invalidateIns(patientId)
      onUpdated()
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className='border rounded-xl p-4 space-y-3'>
      <div className='flex items-center justify-between'>
        <div className='flex items-center gap-3'>
          <h3 className='font-semibold text-gray-800'>
            Identité Nationale de Santé (INS)
          </h3>
          <span
            className='px-2 py-0.5 rounded-full text-xs font-semibold'
            style={{ color: cfg.color, backgroundColor: cfg.bg }}
          >
            {cfg.label}
          </span>
        </div>
        <div className='flex gap-2'>
          {insStatut !== 'QUALIFIE' && (
            <button
              onClick={() => setOpen(!open)}
              className='text-sm px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700'
            >
              Qualifier
            </button>
          )}
          {insStatut === 'QUALIFIE' && (
            <button
              onClick={handleInvalidate}
              disabled={loading}
              className='text-sm px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200'
            >
              Invalider
            </button>
          )}
        </div>
      </div>

      {insMatricule && (
        <p className='text-sm text-gray-600 font-mono bg-gray-50 px-3 py-1.5 rounded-lg'>
          Matricule : <span className='font-semibold'>{insMatricule}</span>
        </p>
      )}

      {open && (
        <div className='border-t pt-3 space-y-3'>
          <p className='text-xs text-gray-500 bg-blue-50 p-2 rounded'>
            Les traits INS doivent correspondre exactement à l'état civil (nom
            de naissance en majuscules).
          </p>

          <div className='grid grid-cols-2 gap-3'>
            <div>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                Matricule INS *
              </label>
              <input
                className='w-full border rounded-lg px-3 py-2 text-sm font-mono'
                placeholder='15 chiffres'
                maxLength={15}
                value={form.insMatricule}
                onChange={e =>
                  setForm({
                    ...form,
                    insMatricule: e.target.value.replace(/\D/g, ''),
                  })
                }
              />
            </div>
            <div>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                Type OID *
              </label>
              <select
                className='w-full border rounded-lg px-3 py-2 text-sm'
                value={form.insOid}
                onChange={e => setForm({ ...form, insOid: e.target.value })}
              >
                <option value={OID_NIR}>INS-NIR (Numéro sécu)</option>
                <option value={OID_NIA}>INS-NIA (Numéro d'attente)</option>
              </select>
            </div>
            <div>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                Nom de naissance *
              </label>
              <input
                className='w-full border rounded-lg px-3 py-2 text-sm uppercase'
                placeholder='DUPONT'
                value={form.birthLastName}
                onChange={e =>
                  setForm({
                    ...form,
                    birthLastName: e.target.value.toUpperCase(),
                  })
                }
              />
            </div>
            <div>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                Prénom(s) de naissance *
              </label>
              <input
                className='w-full border rounded-lg px-3 py-2 text-sm'
                placeholder='Marie Jeanne'
                value={form.birthFirstName}
                onChange={e =>
                  setForm({ ...form, birthFirstName: e.target.value })
                }
              />
            </div>
            <div>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                Date de naissance *
              </label>
              <input
                className='w-full border rounded-lg px-3 py-2 text-sm'
                placeholder='YYYY-MM-DD'
                value={form.birthDate}
                onChange={e => setForm({ ...form, birthDate: e.target.value })}
              />
            </div>
            <div>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                Lieu de naissance (INSEE) *
              </label>
              <input
                className='w-full border rounded-lg px-3 py-2 text-sm font-mono'
                placeholder='75056'
                maxLength={5}
                value={form.birthPlace}
                onChange={e => setForm({ ...form, birthPlace: e.target.value })}
              />
            </div>
            <div>
              <label className='block text-xs font-medium text-gray-600 mb-1'>
                Sexe *
              </label>
              <select
                className='w-full border rounded-lg px-3 py-2 text-sm'
                value={form.gender}
                onChange={e =>
                  setForm({ ...form, gender: e.target.value as Gender })
                }
              >
                <option value='M'>Masculin</option>
                <option value='F'>Féminin</option>
                <option value='U'>Indéterminé</option>
              </select>
            </div>
          </div>

          {error && <p className='text-sm text-red-600'>{error}</p>}

          <div className='flex gap-2 justify-end'>
            <button
              onClick={() => setOpen(false)}
              className='text-sm px-3 py-1.5 border rounded-lg hover:bg-gray-50'
            >
              Annuler
            </button>
            <button
              onClick={handleQualify}
              disabled={loading}
              className='text-sm px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50'
            >
              {loading ? 'Qualification...' : 'Confirmer la qualification'}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
